// @flow
import jwtDecode, { JwtPayload } from 'jwt-decode';
import { PlatformAdapter } from './PlatformAdapter';

export type ExtendedJwtPayload = JwtPayload & {
    appBaseUrl?: string,
    appVersion?: string,
};

const getTokenFromUrl = (): string | undefined => {
    const urlParams = new URLSearchParams(window.location.search);
    const jwt = urlParams.get('jwt') || undefined;

    if (jwt) {
        const jwtDecoded = jwtDecode<JwtPayload>(jwt);
        const { exp } = jwtDecoded;
        const expired = Date.now() >= (exp ?? 0) * 1000;
        if (expired) {
            return undefined;
        }
    }

    return jwt;
};

class TokenManager {
    adapter: PlatformAdapter;

    tokenRefreshCheckInterval = 5_000; // 5 seconds

    tokenRefreshCheckThreshold = 60_000; // 1 minute

    isRefreshingToken = false; // Lock flag to prevent multiple refreshes

    tokenExpirationTime: number | null = null;

    constructor(adapter: PlatformAdapter) {
        this.adapter = adapter;
    }

    async initializeToken(): Promise<void> {
        const jwtToken = getTokenFromUrl();
        if (jwtToken) {
            this.updateToken(jwtToken);
        }
        if (!window.appToken) {
            await this.refreshToken();
        }

        this.startTokenRefresh();
    }

    startTokenRefresh(): void {
        setInterval(() => {
            // Refresh token if it's about to expire
            if (this.tokenExpirationTime && Date.now() > this.tokenExpirationTime - this.tokenRefreshCheckThreshold) {
                this.refreshToken();
            }
        }, this.tokenRefreshCheckInterval);
    }

    updateToken(token: string) {
        if (!this.isRefreshingToken && typeof token === 'string') {
            this.isRefreshingToken = true; // Lock refresh
            try {
                // Decode the token and extract 'exp'
                const jwtDecoded = jwtDecode<ExtendedJwtPayload>(token);
                this.tokenExpirationTime = jwtDecoded.exp ? jwtDecoded.exp * 1000 : null; // Convert exp to milliseconds

                window.appToken = token;
                window.appBaseUrl = jwtDecoded?.appBaseUrl;
                window.appVersion = jwtDecoded?.appVersion;
            } finally {
                this.isRefreshingToken = false; // Unlock after refresh
            }
        }
    }

    async refreshToken() {
        if (!this.isRefreshingToken) {
            const token = await this.adapter.getToken();
            return this.updateToken(token);
        }
    }

    waitForTokenRefresh() {
        return new Promise<void>((resolve) => {
            const checkInterval = setInterval(() => {
                if (!this.isRefreshingToken) {
                    clearInterval(checkInterval);
                    resolve();
                }
            }, 100);
        });
    }
}
export default TokenManager;
